// oxlint-disable react/no-array-index-key -- index is the identity
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { forwardRef, memo, useEffect, useRef, useState } from "react";
import type { ConnectorWithOAuth } from "../../../shared/connectors/getTargetValue";
import { compact } from "../../lib/compact";
import { uniqueByType } from "../../lib/connector";
import { describeCron } from "../../lib/describeCron";
import { routeAuthLabel } from "../../lib/routeAuthDisplay";
import type { CardRect } from "../../lib/routeConnectors";
import { ROUTE_TYPE_ICONS, routeTypeLabel } from "../../lib/routeTypeDisplay";
import {
  playClick,
  playStepSound,
  type StepSoundParams,
  stepSoundBank,
} from "../../lib/sounds";
import {
  advanceStepCompletion,
  type StepCompletionBaseline,
} from "./stepCompletionSignal";
import { EdgeFlowTooltip } from "./EdgeFlowTooltip";
import { EndpointBadges } from "./EndpointBadges";
import { StepHoverCard } from "./StepHoverCard";
import type { StepMapDensity } from "./stepMapGeometry";
import { Blob } from "../Blob";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { META_SEP } from "../MetaSeparator";
import { ReadmeDescriptionTooltip } from "../ReadmeDescriptionTooltip";
import { StepPresence } from "../StepPresence";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { StepData } from "../Workflow";

const pickSound = () =>
  stepSoundBank[Math.floor(Math.random() * stepSoundBank.length)]!;

// Flow highlights wait for hover intent so panning sweeps don't strobe the map.
export const HOVER_INTENT_MS = 250;

const gridStepLineHeight = 12;

const cardPadding: Record<StepMapDensity, number> = {
  normal: 8,
  medium: 6,
  small: 6,
};

const pendingPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const completionPulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  45% { transform: scale(1.012); opacity: 0.9; }
  100% { transform: scale(1); opacity: 1; }
`;

const cardEntrance = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

const Container = styled(Link)`
  text-decoration: none;
  --grid-marker-size: 4px;
  position: absolute;
  border-radius: var(--card-radius, 12px);
  --card-bg: var(--200);
  background: var(--card-bg);
  color: var(--1000);
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 0 3px var(--surface-primary);
  overflow: visible;
  &:hover,
  &:focus-within {
    z-index: 2;
    --card-bg: var(--400);
  }
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 16px;
    z-index: -1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 120ms ease;
  }
  &::before {
    left: 0;
    border-radius: var(--card-radius, 12px) 0 0 var(--card-radius, 12px);
    background: linear-gradient(
      to right,
      color-mix(in srgb, var(--600) 32%, transparent),
      transparent
    );
  }
  &::after {
    right: 0;
    border-radius: 0 var(--card-radius, 12px) var(--card-radius, 12px) 0;
    background: linear-gradient(
      to left,
      color-mix(in srgb, var(--600) 32%, transparent),
      transparent
    );
  }
  &[data-glow="up"]::before {
    opacity: 1;
  }
  &[data-glow="down"]::after {
    opacity: 1;
  }
  &.selected {
    --card-bg: var(--400);
    box-shadow:
      0 0 0 3px var(--surface-primary),
      inset 0 0 0 1px var(--600);
    color: var(--1000);
  }
  &.selected.changed {
    box-shadow: 0 0 0 3px var(--surface-primary);
  }
  /* The one match Enter/Shift+Enter is currently on: a ring in the card's own
     colour, so it reads as "current" among the other (undimmed) matches. */
  &.current-match {
    box-shadow:
      0 0 0 3px var(--surface-primary),
      0 0 0 5px var(--600);
  }
  .dashed-border {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: visible;
  }
  .dashed-border rect {
    stroke: var(--400);
  }
  &:hover .dashed-border rect,
  &:focus-within .dashed-border rect,
  &.selected .dashed-border rect {
    stroke: var(--600);
  }

  .counter {
    background: var(--400);
    color: var(--1000);
    font-size: 9px;
    font-style: normal;
    font-weight: 500;
    line-height: ${gridStepLineHeight}px;
    letter-spacing: -1%;
    display: inline-flex;
    min-width: 16px;
    width: fit-content;
    border-radius: 10px;
    padding: 2px 4px;
    justify-content: center;
  }

  &:hover .counter,
  &:focus-within .counter {
    background: color-mix(in srgb, var(--600) 32%, transparent);
  }

  &.selected .counter {
    background: var(--600);
  }

  &.executed {
    animation: ${completionPulse} 260ms ease-out;
    transform-origin: center;
  }

  &.entering {
    animation: ${cardEntrance} 240ms ease-out both;
  }
`;

const PresenceContainer = styled("div")`
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 1;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

type Props = {
  step: StepData;
  to: string;
  isSelected: boolean;
  isChanged: boolean;
  rect: CardRect;
  density: StepMapDensity;
  cardRadius: number;
  incomingFromStepIds: string[];
  upstreamCount: number;
  downstreamCount: number;
  stepColorMap: Map<string, string>;
  stepNameMap: Map<string, string>;
  onContextMenu: (e: React.MouseEvent, step: StepData) => void;
  isEntering?: boolean;
  entranceDelay?: number;
  isDimmed?: boolean;
  isCurrentMatch?: boolean;
  onHighlightHover?: (
    canonicalId: string,
    hovered: boolean,
    direction: "up" | "down" | "both"
  ) => void;
  isAiPresent: boolean;
  presentUserIds: Set<string>;
  enableSounds: boolean;
  copyableUrl: string | null;
  copyableEmail: string | null;
};

export const GridStep = memo(
  forwardRef<HTMLAnchorElement, Props>((props, ref) => {
    const {
      step,
      to,
      isSelected,
      isChanged,
      rect,
      density,
      cardRadius,
      incomingFromStepIds,
      upstreamCount,
      downstreamCount,
      stepColorMap,
      stepNameMap,
      onContextMenu,
      isEntering,
      entranceDelay,
      isDimmed,
      isCurrentMatch,
      onHighlightHover,
      isAiPresent,
      presentUserIds,
      enableSounds,
      copyableUrl,
      copyableEmail,
    } = props;

    const color = step.color;
    const latestStatus = step.lastStatus;
    const latestCompletedAt = step.lastCompletedAt;
    const isRun = step.lastKind !== "build";
    const isExecuting = isRun && latestStatus === "pending";
    const [showCompletionPulse, setShowCompletionPulse] = useState(false);
    const [edgeGlow, setEdgeGlow] = useState<"up" | "down" | "both" | null>(
      null
    );
    const [isHovered, setIsHovered] = useState(false);
    const [hoverZone, setHoverZone] = useState<"up" | "down" | "both" | null>(
      null
    );
    const [overChip, setOverChip] = useState(false);
    const completionBaselineRef = useRef<StepCompletionBaseline>({
      ready: false,
      completedAt: null,
    });
    const soundRef = useRef<StepSoundParams>(pickSound());
    const cardRef = useRef<HTMLAnchorElement>(null);
    const [cardAnchorName] = useState(() => `--step-card-${nanoid(8)}`);
    const setCardRef = (node: HTMLAnchorElement | null) => {
      cardRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    useEffect(() => {
      const result = advanceStepCompletion(
        completionBaselineRef.current,
        enableSounds,
        latestCompletedAt
      );
      completionBaselineRef.current = result.baseline;
      if (result.completed) {
        setShowCompletionPulse(true);
        playStepSound(soundRef.current);
      }
    }, [latestCompletedAt, enableSounds]);

    const hoverIntentRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingDirRef = useRef<"up" | "down" | "both">("both");
    const activeDirRef = useRef<"up" | "down" | "both" | null>(null);

    // An edge only picks a direction when the card actually has a neighbor
    // that way; otherwise it reads as a plain middle hover (no dim, no glow).
    const hoverDirection = (
      e: React.MouseEvent<HTMLAnchorElement>
    ): "up" | "down" | "both" => {
      const r = e.currentTarget.getBoundingClientRect();
      const edge = Math.min(48, r.width * 0.3);
      const x = e.clientX - r.left;
      const raw = x < edge ? "up" : x > r.width - edge ? "down" : "both";
      if (raw === "up" && upstreamCount === 0) return "both";
      if (raw === "down" && downstreamCount === 0) return "both";
      return raw;
    };

    const scheduleHighlight = (dir: "up" | "down" | "both") => {
      setHoverZone(dir);
      if (activeDirRef.current === dir) {
        if (hoverIntentRef.current) {
          clearTimeout(hoverIntentRef.current);
          hoverIntentRef.current = null;
        }
        return;
      }
      if (hoverIntentRef.current && pendingDirRef.current === dir) return;
      pendingDirRef.current = dir;
      if (hoverIntentRef.current) clearTimeout(hoverIntentRef.current);
      hoverIntentRef.current = setTimeout(() => {
        hoverIntentRef.current = null;
        activeDirRef.current = dir;
        setEdgeGlow(dir);
        onHighlightHover?.(step.canonicalId, true, dir);
      }, HOVER_INTENT_MS);
    };

    // A connector logo or badge sitting in an edge zone owns its own tooltip, so
    // the edge highlight (and the details flyout) stands down while over one.
    // Portaled tooltip events bubble through React despite living outside the card.
    const isCardHoverEvent = (e: React.MouseEvent<HTMLAnchorElement>) =>
      e.currentTarget.contains(e.target as Node);

    const updateHover = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!isCardHoverEvent(e)) return;
      const chip = (e.target as Element).closest("[data-card-chip]") !== null;
      setOverChip(chip);
      scheduleHighlight(chip ? "both" : hoverDirection(e));
    };

    const onHoverEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!isCardHoverEvent(e)) return;
      setIsHovered(true);
      updateHover(e);
    };

    const onHoverMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
      updateHover(e);
    };

    const onHoverLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!isCardHoverEvent(e)) return;
      setIsHovered(false);
      setHoverZone(null);
      setOverChip(false);
      if (hoverIntentRef.current) {
        clearTimeout(hoverIntentRef.current);
        hoverIntentRef.current = null;
      }
      if (activeDirRef.current !== null) {
        activeDirRef.current = null;
        setEdgeGlow(null);
        onHighlightHover?.(step.canonicalId, false, "both");
      }
    };

    useEffect(
      () => () => {
        if (hoverIntentRef.current) clearTimeout(hoverIntentRef.current);
      },
      []
    );

    const connectedAvatarApps = uniqueByType(
      compact(
        (step.connectedApps ?? []).map((c) => c.connectedApp)
      ) as ConnectorWithOAuth[]
    ).map((app) => ({ id: app.id, name: app.name, app }));
    const disconnectedAvatarSlots = [
      ...new Set(
        (step.connectedApps ?? [])
          .filter((c) => !c.connectedApp)
          .map((c) => c.appType)
      ),
    ].map((type) => ({ id: `missing-${type}`, appType: type }));

    // Edge highlight is active (glow shown) or pending (cursor on the edge, glow
    // not yet armed); either way the details flyout must stay out of its way.
    const edgeActive =
      hoverZone === "up" ||
      hoverZone === "down" ||
      edgeGlow === "up" ||
      edgeGlow === "down";

    return (
      <Container
        ref={setCardRef}
        to={to}
        className={`${isSelected ? "selected" : ""}${isChanged ? " changed" : ""}${showCompletionPulse ? " executed" : ""}${isEntering ? " entering" : ""}${isCurrentMatch ? " current-match" : ""}`}
        data-glow={edgeGlow ?? undefined}
        data-density={density}
        data-step-id={step.id}
        onMouseEnter={onHoverEnter}
        onMouseMove={onHoverMove}
        onMouseLeave={onHoverLeave}
        onContextMenu={(e) => {
          e.preventDefault();
          playClick();
          onContextMenu(e, step);
        }}
        onAnimationEnd={() => setShowCompletionPulse(false)}
        style={
          {
            "--200": `var(--${color}-200)`,
            "--400": `var(--${color}-400)`,
            "--600": `var(--${color}-600)`,
            "--1000": `var(--${color}-1000)`,
            "--card-radius": `${cardRadius}px`,
            anchorName: cardAnchorName,
            left: rect.left,
            top: rect.top,
            width: rect.right - rect.left,
            height: rect.bottom - rect.top,
            zIndex: isSelected ? 2 : 1,
            padding: cardPadding[density],
            opacity: isDimmed ? 0.6 : undefined,
            filter: isDimmed ? "blur(3px)" : undefined,
            animationDelay:
              isEntering && entranceDelay ? `${entranceDelay}ms` : undefined,
          } as React.CSSProperties
        }
      >
        {isChanged && (
          <svg className="dashed-border" width="100%" height="100%">
            <rect
              x="0.5"
              y="0.5"
              width="calc(100% - 1px)"
              height="calc(100% - 1px)"
              rx={cardRadius - 0.5}
              fill="none"
              strokeWidth="1"
              strokeDasharray="4 2"
            />
          </svg>
        )}
        <PresenceContainer>
          <StepPresence
            presentUserIds={presentUserIds}
            isAiPresent={isAiPresent}
            avatarSize={12}
          />
        </PresenceContainer>

        {density !== "normal" && (
          <StepHoverCard
            step={step}
            hovered={isHovered && !edgeActive && !overChip}
            anchorName={cardAnchorName}
            anchorRef={cardRef}
            incomingFromStepIds={incomingFromStepIds}
            isChanged={isChanged}
            connected={connectedAvatarApps}
            disconnected={disconnectedAvatarSlots}
          />
        )}

        {(edgeGlow === "up" || edgeGlow === "down") && !overChip && (
          <EdgeFlowTooltip
            anchorName={cardAnchorName}
            anchorRef={cardRef}
            direction={edgeGlow}
            count={edgeGlow === "up" ? upstreamCount : downstreamCount}
          />
        )}

        <div
          style={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            gap: 6,
            animation: isExecuting
              ? `${pendingPulse} 0.85s ease-in-out infinite`
              : undefined,
          }}
        >
          <Blob colors={[`var(--${color}-600)`]} />

          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              ...(density === "normal"
                ? {
                    flexDirection: "column" as const,
                    justifyContent: "space-between",
                  }
                : { alignItems: "center", gap: 6 }),
            }}
          >
            <div
              style={{
                flex: density !== "normal" ? 1 : undefined,
                minWidth: 0,
              }}
            >
              <Text
                variant={
                  density === "small" ? "text-smaller-md" : "text-small-md"
                }
              >
                <div
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    ...(isExecuting
                      ? {
                          background: `linear-gradient(90deg, var(--1000) 30%, var(--600) 50%, var(--1000) 70%)`,
                          backgroundSize: "200% 100%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: `${shimmer} 2.5s linear infinite`,
                        }
                      : undefined),
                  }}
                >
                  {step.name}
                </div>
              </Text>
            </div>
            {density !== "normal" && (
              <EndpointBadges
                step={step}
                incomingFromStepIds={incomingFromStepIds}
                copyableUrl={copyableUrl}
                copyableEmail={copyableEmail}
              />
            )}
            {density !== "normal" &&
              (connectedAvatarApps.length > 0 ||
                disconnectedAvatarSlots.length > 0) && (
                <ConnectorAvatars
                  flat
                  collapseToFit
                  align="end"
                  fillAvailableWidth={false}
                  connected={connectedAvatarApps}
                  disconnected={disconnectedAvatarSlots}
                />
              )}
            {density === "normal" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  gap: 6,
                }}
              >
                <div className="counter">
                  {step.executionCount.toLocaleString()}
                </div>

                <div
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                  data-card-chip
                >
                  <EndpointBadges
                    step={step}
                    incomingFromStepIds={incomingFromStepIds}
                    copyableUrl={copyableUrl}
                    copyableEmail={copyableEmail}
                  />

                  {incomingFromStepIds.length > 1 && (
                    <Tooltip
                      content={
                        <span style={{ display: "block" }}>
                          <Text
                            variant="text-small"
                            color="rgba(255, 255, 255, 0.5)"
                          >
                            Multiple inputs:
                          </Text>
                          <br />
                          <Text variant="text-small-md">
                            {incomingFromStepIds
                              .map((id) => stepNameMap.get(id) ?? "Unknown")
                              .join(", ")}
                          </Text>
                        </span>
                      }
                    >
                      <SourceCircles
                        fromStepIds={incomingFromStepIds}
                        stepColorMap={stepColorMap}
                        selfColor={color}
                      />
                    </Tooltip>
                  )}

                  {step.schedule?.cronExpression && (
                    <Tooltip
                      content={describeCron(step.schedule.cronExpression)}
                    >
                      <span style={{ display: "flex" }}>
                        <Icon
                          name="clock"
                          size={12}
                          color={`var(--${color}-1000)`}
                        />
                      </span>
                    </Tooltip>
                  )}

                  {step.hasConflicts && (
                    <Tooltip content="This step has unresolved merge conflicts">
                      <span style={{ display: "flex" }}>
                        <Icon
                          name="merge"
                          size={12}
                          color={`var(--${color}-1000)`}
                        />
                      </span>
                    </Tooltip>
                  )}

                  {(step.hasOutput || step.routeType) && (
                    <Tooltip
                      content={
                        step.routeType
                          ? `${routeTypeLabel(step.routeType)}${META_SEP}${routeAuthLabel(step.routeAuth)}`
                          : "Responds to web request"
                      }
                    >
                      <span style={{ display: "flex" }}>
                        <Icon
                          name={
                            step.routeType
                              ? ROUTE_TYPE_ICONS[step.routeType]
                              : "earth"
                          }
                          size={12}
                          color={`var(--${color}-1000)`}
                        />
                      </span>
                    </Tooltip>
                  )}

                  {step.hasStorage && (
                    <Tooltip content="Storage">
                      <span style={{ display: "flex" }}>
                        <Icon
                          name="box"
                          size={12}
                          color={`var(--${color}-1000)`}
                        />
                      </span>
                    </Tooltip>
                  )}

                  {isChanged && (
                    <Tooltip content="Changed on branch">
                      <span style={{ display: "flex" }}>
                        <Icon
                          name="traffic-cone"
                          size={12}
                          color={`var(--${color}-1000)`}
                        />
                      </span>
                    </Tooltip>
                  )}

                  {step.readmeDescription && (
                    <ReadmeDescriptionTooltip
                      description={step.readmeDescription}
                      iconColor={`var(--${color}-1000)`}
                      anchorName={cardAnchorName}
                      anchorRef={cardRef}
                    />
                  )}
                </div>

                <ConnectorAvatars
                  flat
                  collapseToFit
                  align="end"
                  connected={connectedAvatarApps}
                  disconnected={disconnectedAvatarSlots}
                />
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  })
);

const CircleStack = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 3px;
  margin: 0 -2px;
  border-radius: 9999px;
  &:hover {
    --card-bg: color-mix(in srgb, var(--600) 32%, var(--400));
    background: var(--card-bg);
  }
`;

function SourceCircles({
  fromStepIds,
  stepColorMap,
  selfColor,
}: {
  fromStepIds: string[];
  stepColorMap: Map<string, string>;
  selfColor: string;
}) {
  const colors = fromStepIds.map((id) => stepColorMap.get(id) ?? "gray");
  colors.push(selfColor);
  return (
    <CircleStack>
      {colors.map((c, i) => (
        <span
          key={`${i}-${c}`}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: `var(--${c}-600)`,
            marginLeft: i > 0 ? -4 : 0,
            boxShadow: i > 0 ? "0 0 0 1px var(--card-bg)" : undefined,
          }}
        />
      ))}
    </CircleStack>
  );
}
